import { Search, X, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useMemo, useState } from 'react';

interface CategorySelectorProps {
  selectedCategories: string[];
  onToggleCategory: (category: string) => void;
  allCategories: string[];
}

export function CategorySelector({
  selectedCategories,
  onToggleCategory,
  allCategories,
}: CategorySelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [pressedChip, setPressedChip] = useState<string | null>(null);

  // Filter categories based on search
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return allCategories;

    const query = searchQuery.toLowerCase();
    return allCategories.filter((cat) => cat.toLowerCase().includes(query));
  }, [allCategories, searchQuery]);

  // Group categories alphabetically
  const groupedCategories = useMemo(() => {
    const groups: { [key: string]: string[] } = {};

    filteredCategories.forEach((category) => {
      const firstLetter = category[0].toUpperCase();
      if (!groups[firstLetter]) {
        groups[firstLetter] = [];
      }
      groups[firstLetter].push(category);
    });

    Object.keys(groups).forEach((letter) => {
      groups[letter].sort();
    });

    return groups;
  }, [filteredCategories]);

  const alphabeticalOrder = Object.keys(groupedCategories).sort();

  const handleRemoveCategory = (category: string) => {
    onToggleCategory(category);
  };

  return (
    <div
      style={{
        paddingLeft: '20px',
        paddingRight: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px', // space-y-4
      }}
    >
      {/* Header with count */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <label
          style={{
            display: 'block',
            fontSize: 'var(--text-sm)',
            color: 'var(--muted-foreground)',
          }}
        >
          Categorias selecionadas ({selectedCategories.length})
        </label>

        {selectedCategories.length > 0 && (
          <button
            type="button"
            onClick={() =>
              selectedCategories.forEach((cat) => onToggleCategory(cat))
            }
            style={{
              fontSize: '0.75rem', // text-xs
              lineHeight: '1rem',
              color: '#2563eb', // blue-600
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
          >
            Limpar todas
          </button>
        )}
      </div>

      {/* Search Bar */}
      <div
        style={{
          position: 'relative',
          paddingLeft: '0.25rem', // px-1
          paddingRight: '0.25rem',
        }}
      >
        <Search
          style={{
            position: 'absolute',
            left: '16px', // left-4
            top: '50%',
            transform: 'translateY(-50%)',
            width: '16px',
            height: '16px',
            color: '#9ca3af', // gray-400
          }}
        />

        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsSearchFocused(true)}
          onBlur={() => setIsSearchFocused(false)}
          placeholder="Buscar categorias..."
          aria-label="Buscar categorias"
          style={{
            width: '100%',
            paddingLeft: '40px', // pl-10
            paddingRight: '16px', // pr-4
            paddingTop: '12px', // py-3
            paddingBottom: '12px',
            border: isSearchFocused
              ? '2px solid #3b82f6' // ring-blue-500
              : '1px solid #d1d5db', // gray-300
            borderRadius: '12px', // rounded-xl
            outline: 'none',
            boxSizing: 'border-box',
          }}
        />

        {searchQuery && (
          <motion.button
            type="button"
            onClick={() => setSearchQuery('')}
            style={{
              position: 'absolute',
              right: '16px', // right-4
              top: '50%',
              transform: 'translateY(-50%)',
              width: '20px', // w-5
              height: '20px', // h-5
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '9999px',
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: 0,
            }}
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            whileTap={{ scale: 0.9 }}
          >
            <X
              style={{
                width: '12px', // w-3
                height: '12px', // h-3
                color: '#6b7280', // gray-500
              }}
            />
          </motion.button>
        )}
      </div>

      {/* Selected Categories Chips */}
      {selectedCategories.length > 0 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px', // gap-2
            padding: '12px', // p-3
            backgroundColor: '#eff6ff', // blue-50
            border: '1px solid #bfdbfe', // blue-200
            borderRadius: '12px', // rounded-xl
          }}
        >
          <AnimatePresence mode="popLayout">
            {selectedCategories.map((category) => (
              <motion.div
                key={category}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px', // gap-1.5
                  paddingLeft: '12px', // px-3
                  paddingRight: '12px',
                  paddingTop: '6px', // py-1.5
                  paddingBottom: '6px',
                  backgroundColor: '#2563eb', // blue-600
                  color: '#ffffff',
                  borderRadius: '9999px',
                  fontSize: '0.875rem', // text-sm
                  lineHeight: '1.25rem',
                }}
              >
                <span>{category}</span>

                <button
                  type="button"
                  onClick={() => handleRemoveCategory(category)}
                  onMouseDown={() => setPressedChip(category)}
                  onMouseUp={() => setPressedChip(null)}
                  onMouseLeave={() => setPressedChip(null)}
                  aria-label={`Remover ${category}`}
                  style={{
                    borderRadius: '9999px',
                    padding: '2px', // p-0.5
                    backgroundColor:
                      pressedChip === category ? '#1d4ed8' : 'transparent', // active:bg-blue-700
                    border: 'none',
                    cursor: 'pointer',
                    transition: 'background-color 150ms ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <X style={{ width: '12px', height: '12px', color: '#fff' }} />
                </button>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}

      {/* Scrollable Category List */}
      <div
        style={{
          maxHeight: '350px', // max-h-[350px]
          overflowY: 'auto',
          marginLeft: '-24px', // -mx-6
          marginRight: '-24px',
          paddingLeft: '24px', // px-6
          paddingRight: '24px',
          border: '1px solid #e5e7eb', // gray-200
          borderRadius: '12px', // rounded-xl
          paddingBottom: '96px', // pb-24
          boxSizing: 'border-box',
        }}
      >
        {filteredCategories.length === 0 ? (
          <div
            style={{
              paddingTop: '32px', // py-8
              paddingBottom: '32px',
              textAlign: 'center',
              color: '#6b7280', // gray-500
              fontSize: '0.875rem', // text-sm
              lineHeight: '1.25rem',
            }}
          >
            Nenhuma categoria encontrada
          </div>
        ) : (
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px', // space-y-1
              paddingTop: '8px', // py-2
              paddingBottom: '8px',
            }}
          >
            {alphabeticalOrder.map((letter) => (
              <div key={letter}>
                {/* Letter Header */}
                <div
                  style={{
                    position: 'sticky',
                    top: 0,
                    background:
                      'linear-gradient(to bottom, #ffffff 0%, #ffffff 70%, rgba(255,255,255,0.8) 100%)',
                    paddingTop: '8px', // py-2
                    paddingBottom: '8px',
                    zIndex: 10,
                  }}
                >
                  <h3
                    style={{
                      fontSize: '0.75rem', // text-xs
                      lineHeight: '1rem',
                      color: '#6b7280', // gray-500
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em', // tracking-wider
                      paddingLeft: '8px', // px-2
                      paddingRight: '8px',
                      margin: 0,
                    }}
                  >
                    {letter}
                  </h3>
                </div>

                {/* Categories under this letter */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '2px', // space-y-0.5
                  }}
                >
                  {groupedCategories[letter].map((category) => {
                    const isSelected = selectedCategories.includes(category);

                    return (
                      <motion.button
                        key={category}
                        type="button"
                        onClick={() => onToggleCategory(category)}
                        whileTap={{ scale: 0.98 }}
                        role="checkbox"
                        aria-checked={isSelected}
                        aria-label={category}
                        style={{
                          width: '100%',
                          paddingLeft: '12px', // px-3
                          paddingRight: '12px',
                          paddingTop: '10px', // py-2.5
                          paddingBottom: '10px',
                          borderRadius: '8px', // rounded-lg
                          textAlign: 'left',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: '12px', // gap-3
                          border: 'none',
                          cursor: 'pointer',
                          transition:
                            'background-color 150ms ease, color 150ms ease',
                          backgroundColor: isSelected
                            ? '#eff6ff'
                            : 'transparent', // blue-50
                          color: isSelected ? '#1e3a8a' : '#111827', // blue-900 / gray-900
                        }}
                      >
                        <span
                          style={{
                            fontWeight: isSelected ? 500 : 400, // font-medium
                          }}
                        >
                          {category}
                        </span>

                        {isSelected && (
                          <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{
                              type: 'spring',
                              stiffness: 500,
                              damping: 25,
                            }}
                            style={{
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Check
                              style={{
                                width: '16px',
                                height: '16px',
                                color: '#2563eb', // blue-600
                              }}
                            />
                          </motion.div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
